import { useCallback, useEffect } from 'react';
import { FieldPath, FieldValues, Path, PathValue, UseFormReturn } from 'react-hook-form';
import { getCoordinatesForAddress } from '../utils/googlePlaces';
import { useIntl } from 'react-intl';
import { OnAutocompleteInputChange } from '../types/autocomplete';
import { Subject } from 'rxjs';

export enum AutoAddressFields {
  address = 'address',
  city = 'city',
  state = 'state',
  coordinates = 'latitudeLongitude',
}

export function useAutoResolveCoordinates<T extends FieldValues>(
  methods: UseFormReturn<T>,
  config?: {
    fieldPrefix?: string;
    fieldKeyOverride?: {
      [key in AutoAddressFields]?: string;
    };
    focusInput?: Subject<boolean>;
    isManualCoordinates?: boolean;
  },
) {
  const { formatMessage } = useIntl();

  const getFieldKey = (field: AutoAddressFields): FieldPath<T> => {
    return [config?.fieldPrefix, config?.fieldKeyOverride?.[field] || field]
      .filter(Boolean)
      .join('.') as FieldPath<T>;
  };

  const onAddressInputBlur = () => {
    methods.trigger([
      getFieldKey(AutoAddressFields.address),
      getFieldKey(AutoAddressFields.coordinates),
    ]);
  };

  const onAddressInputChange: OnAutocompleteInputChange = (value, metadata) => {
    if (value.trim() === '' && metadata?.isActive) {
      methods.setValue(getFieldKey(AutoAddressFields.coordinates), '' as PathValue<T, Path<T>>, {
        shouldValidate: true,
      });
      methods.trigger([
        getFieldKey(AutoAddressFields.address),
        getFieldKey(AutoAddressFields.coordinates),
      ]);
    }
    if (value.trim() !== '' && !metadata?.isActive) {
      methods.setValue(getFieldKey(AutoAddressFields.coordinates), '' as PathValue<T, Path<T>>, {
        shouldValidate: true,
      });
      methods.trigger([
        getFieldKey(AutoAddressFields.address),
        getFieldKey(AutoAddressFields.coordinates),
      ]);
      config?.focusInput?.next(true);
    }
  };

  // Function to resolve coordinates and update the form
  const resolveCoordinates = useCallback(async () => {
    const address = methods.getValues(getFieldKey(AutoAddressFields.address)) as string;
    const city = methods.getValues(getFieldKey(AutoAddressFields.city)) as string;
    const state = methods.getValues(getFieldKey(AutoAddressFields.state)) as string;
    try {
      if (!address || !city || !state) {
        throw new Error('All required fields (address, city, state) must be provided.');
      }
      const { latitude, longitude, resolvedSuccessfully } = await getCoordinatesForAddress(
        address,
        city,
        state,
      );
      if (!resolvedSuccessfully) {
        throw new Error('Failed to resolve coordinates.');
      }

      if (!config?.isManualCoordinates) {
        const latLongStr = `${latitude}, ${longitude}`;
        methods.setValue(
          getFieldKey(AutoAddressFields.coordinates) as FieldPath<T>,
          latLongStr as PathValue<T, Path<T>>,
        );
      }
      return true;
    } catch (error) {
      if (config?.isManualCoordinates) {
        methods.setError(getFieldKey(AutoAddressFields.coordinates) as FieldPath<T>, {
          type: 'addressCouldNotBeValidated',
          message: formatMessage({ id: 'generic.addressCouldNotBeValidated' }),
        });
      } else {
        methods.setError(getFieldKey(AutoAddressFields.address) as FieldPath<T>, {
          type: 'unresolvedCoordinatesError',
          message: formatMessage({ id: 'generic.unresolvedCoordinatesError' }),
        });
      }
      return false;
    }
  }, [methods, config, formatMessage]);

  useEffect(() => {
    const subscription = methods.watch((_, { name }) => {
      // Watch for changes in any of the address fields or coordinates field
      const watchedFields = [
        getFieldKey(AutoAddressFields.address),
        getFieldKey(AutoAddressFields.city),
        getFieldKey(AutoAddressFields.state),
        getFieldKey(AutoAddressFields.coordinates),
      ];
      if (name && watchedFields.includes(name)) {
        const addressFieldPath = getFieldKey(AutoAddressFields.address);
        if (methods.getFieldState(addressFieldPath)?.error?.type === 'unresolvedCoordinatesError') {
          methods.clearErrors(addressFieldPath);
        }

        const coordinatesFieldPath = getFieldKey(AutoAddressFields.coordinates);
        if (methods.getFieldState(coordinatesFieldPath)?.error?.type === 'addressCouldNotBeValidated') {
          methods.clearErrors(coordinatesFieldPath);
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [methods, config]);

  return { onAddressInputBlur, onAddressInputChange, resolveCoordinates };
}
